import { createAsyncThunk } from '@reduxjs/toolkit';
import { QueryClient } from 'react-query';
import { MultiValue, SingleValue } from 'react-select';
import { DefaultOption } from 'src/components/molecules';
import {
  CaseByIdQuery,
  useAddTagToCaseMutation,
  useRemoveTagFromCaseMutation,
} from 'src/graphql';
import { TRootState } from 'src/store';
import { caseSlice } from '../Case.slice';

type CaseTagUpdatedArgs = {
  caseId: string;
  selectedTagOptions: SingleValue<DefaultOption> | MultiValue<DefaultOption>;
  mutationAddCaseTag: ReturnType<typeof useAddTagToCaseMutation>['mutateAsync'];
  mutationRemoveCaseTag: ReturnType<
    typeof useRemoveTagFromCaseMutation
  >['mutateAsync'];
  queryClient: QueryClient;
};

export const caseTagUpdated = createAsyncThunk<
  void,
  CaseTagUpdatedArgs,
  { state: TRootState }
>(
  'case/tagUpdated',
  async (
    {
      caseId,
      selectedTagOptions,
      mutationAddCaseTag,
      mutationRemoveCaseTag,
      queryClient,
    }: CaseTagUpdatedArgs,
    thunkAPI,
  ) => {
    const state = thunkAPI.getState();
    const selectedTagsIds = caseSlice
      .getSelectors()
      .getSelectedTags(state.case)
      .map((tag) => tag.id);

    const selectedOptionArray = Array.isArray(selectedTagOptions)
      ? selectedTagOptions
      : [selectedTagOptions];
    const newSelectedTagsIds = selectedOptionArray.map(
      (option) => option.value,
    );

    const tags = state.tags.tags || [];

    const tagIdToBeAdded = newSelectedTagsIds.filter(
      (tag) => !selectedTagsIds.includes(tag),
    )?.[0];
    const tagIdToBeRemoved = selectedTagsIds.filter(
      (tag) => !newSelectedTagsIds.includes(tag),
    )?.[0];

    const updateCacheWithAddedTag = (args: {
      tagId: string;
      caseId: string;
    }) => {
      const tag = tags.find((tag) => tag.id === args.tagId);

      queryClient.cancelQueries(['CaseById', { id: caseId }]);
      queryClient.setQueryData<CaseByIdQuery | undefined>(
        ['CaseById', { id: caseId }],
        (oldData) => {
          if (!oldData?.caseById?.CaseTags || !tag) {
            return oldData;
          }

          return {
            ...oldData,
            caseById: {
              ...oldData.caseById,
              CaseTags: [tag, ...oldData.caseById.CaseTags],
            },
          };
        },
      );
    };

    const updateCacheWithRemovedTag = (args: {
      tagId: string;
      caseId: string;
    }) => {
      queryClient.cancelQueries(['CaseById', { id: caseId }]);
      queryClient.setQueryData<CaseByIdQuery | undefined>(
        ['CaseById', { id: caseId }],
        (oldData) => {
          if (!oldData?.caseById?.CaseTags) {
            return oldData;
          }
          return {
            ...oldData,
            caseById: {
              ...oldData.caseById,
              CaseTags: oldData.caseById.CaseTags.filter(
                (oldTag) => oldTag.id !== args.tagId,
              ),
            },
          };
        },
      );
    };

    if (tagIdToBeAdded) {
      try {
        updateCacheWithAddedTag({
          tagId: tagIdToBeAdded,
          caseId: caseId,
        });

        await mutationAddCaseTag({
          caseId,
          tagId: tagIdToBeAdded,
        });

        queryClient.invalidateQueries(['CaseById', { id: caseId }]);
      } catch (e) {
        updateCacheWithRemovedTag({
          tagId: tagIdToBeAdded,
          caseId: caseId,
        });

        throw e;
      }
    } else if (tagIdToBeRemoved) {
      try {
        updateCacheWithRemovedTag({
          tagId: tagIdToBeRemoved,
          caseId: caseId,
        });

        await mutationRemoveCaseTag({
          caseId,
          tagId: tagIdToBeRemoved,
        });

        queryClient.invalidateQueries(['CaseById', { id: caseId }]);
      } catch (e) {
        updateCacheWithAddedTag({
          tagId: tagIdToBeRemoved,
          caseId: caseId,
        });

        throw e;
      }
    }
  },
);
